<template>
    <Layout>
        <div class="space-y-4 p-4 pt-16 lg:space-y-8">
            <div class="relative mx-auto max-w-4xl" ref="clickOutsideTarget">
                <!-- Mobile version -->
                <form class="flex flex-col overflow-hidden rounded-xl border border-black md:hidden" @submit.prevent="handleSearch">
                    <div class="border-b border-black">
                        <div class="relative w-full">
                            <input
                                type="text"
                                name="query"
                                id="query"
                                v-model="query"
                                :placeholder="$t('Job title, company, keyword')"
                                class="h-12 w-full border-none pl-10 focus:border-none focus:outline-none focus:ring-0"
                                @focus="showDropdown = true"
                            />
                            <IconSearch class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <div>
                        <div class="relative w-full">
                            <CityAutocomplete
                                v-model="city"
                                @latlng="setLatLng"
                                class="h-12 pl-10"
                                :placeholder="$t('Search by city')"
                                @focus="showDropdown = false"
                                borderless
                            />
                            <IconMapPin class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <button class="button h-12 rounded-t-none">{{ $t('Search') }}</button>
                </form>

                <!-- Desktop version -->
                <form
                    class="hidden items-center rounded-xl border border-black bg-white p-2 shadow-xl md:flex"
                    @submit.prevent="handleSearch"
                >
                    <div class="flex-1">
                        <div class="relative w-full">
                            <input
                                type="text"
                                name="query"
                                id="query"
                                v-model="query"
                                :placeholder="$t('Job title, company, keyword')"
                                class="h-12 w-full border-none pl-10 focus:border-none focus:outline-none focus:ring-0"
                                @focus="showDropdown = true"
                            />
                            <IconSearch class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>
                    </div>

                    <div class="h-8 w-px bg-gray-200"></div>

                    <div class="flex flex-1 pl-8">
                        <div class="relative w-full">
                            <CityAutocomplete
                                v-model="city"
                                @latlng="setLatLng"
                                class="h-12 pl-10"
                                :placeholder="$t('Search by city')"
                                @focus="showDropdown = false"
                                borderless
                            />
                            <IconMapPin class="pointer-events-none absolute left-3 top-3.5 h-5 w-5 select-none text-gray-500" />
                        </div>

                        <button class="button h-12">{{ $t('Search') }}</button>
                    </div>
                </form>

                <div
                    v-if="showDropdown && recentSavedSearches.length"
                    class="absolute left-0 mt-4 w-[300px] rounded-xl border border-gray-300 bg-white p-4 shadow"
                >
                    <div class="space-y-4">
                        <p class="text-xs font-medium text-gray-700">Recherches récentes</p>

                        <div class="space-y-1">
                            <div v-for="search in recentSavedSearches" class="flex items-center justify-between gap-4">
                                <InertiaLink
                                    :href="getSavedSearchUrl(search)"
                                    class="block w-full rounded-xl p-2 text-sm transition-all hover:bg-gray-50"
                                >
                                    <p>{{ search.query }}</p>
                                    <p v-if="search.city" class="text-xs text-gray-600">{{ search.city }}</p>
                                </InertiaLink>

                                <button class="shrink-0 text-gray-600 hover:text-black" @click.prevent="deleteSearchItem(search.id)">
                                    <IconX class="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="params.recherche || params.ville" class="flex items-center justify-center gap-4">
                <div v-if="params.recherche" class="flex items-center gap-2 rounded bg-white px-4 py-2 text-xs shadow">
                    <p class="text-gray-600">{{ $t('Keywords') }}</p>
                    <p>:</p>
                    <p class="font-medium">{{ params.recherche }}</p>
                    <button @click="clearQuery">&times;</button>
                </div>

                <div v-if="params.ville" class="flex items-center gap-2 rounded bg-white px-4 py-2 text-xs shadow">
                    <p class="text-gray-600">{{ $t('City') }}</p>
                    <p>:</p>
                    <p class="font-medium">{{ params.ville }}</p>
                    <button @click="clearCity">&times;</button>
                </div>
            </div>

            <div v-if="props.company" class="mx-auto flex max-w-5xl items-center gap-4 rounded bg-white p-4 shadow">
                <img :src="props.company.logo" alt="" class="h-10 w-auto object-contain" />
                <p>
                    {{ $t('Showing offers for') }}
                    <strong>{{ props.company.name }}</strong>
                </p>

                <InertiaLink href="/emplois" class="ml-auto font-medium text-gray-800 hover:underline">
                    {{ $t('View the offers for all companies') }}
                </InertiaLink>
            </div>

            <div class="mx-auto max-w-[1200px] space-y-6">
                <div v-if="props.redirect" class="mx-auto max-w-xl rounded-xl border bg-white p-6">
                    <p class="font-medium">
                        {{ $t('The offer') }}
                        <strong>{{ props.redirect }}</strong>
                        {{ $t('is not available right now.') }}
                    </p>
                    <p class="text-sm text-gray-600">{{ $t('Here are similar offers that might interest you.') }}</p>
                </div>

                <div class="grid gap-4 lg:grid-cols-2 lg:gap-8 xl:grid-cols-3">
                    <OfferCard v-for="offer in offers" :key="offer.title" :offer="offer" />

                    <div v-if="!offers.length" class="col-span-full">
                        <!-- If we have a search term, then show the "No offers found matching your search" message -->
                        <div
                            v-if="query.length || city.length"
                            class="mx-auto flex max-w-xl flex-col items-center justify-center gap-6 rounded-xl bg-white p-16 text-center shadow"
                        >
                            <p class="text-lg font-medium">{{ $t('No offers found') }}</p>
                            <p class="text-gray-600">{{ $t('No offers found matching your search criterias.') }}</p>
                            <InertiaLink class="button" :href="$page.props.locale == 'fr' ? '/emplois' : '/en/job-opportunities'">
                                {{ $t('View all the offers') }}
                            </InertiaLink>
                        </div>

                        <!-- Loading state -->
                        <div
                            v-else-if="loading"
                            class="mx-auto flex max-w-xl flex-col items-center justify-center gap-6 rounded-xl bg-white p-16 text-center shadow"
                        >
                            <p class="text-lg font-medium">{{ $t('Loading offers ...') }}</p>
                            <p class="text-gray-600">{{ $t('We are fetching the latest offers, please wait.') }}</p>
                        </div>

                        <!-- No offers available -->
                        <div
                            v-else
                            class="mx-auto flex max-w-xl flex-col items-center justify-center gap-6 rounded-xl bg-white p-16 text-center shadow"
                        >
                            <p class="text-lg font-medium">{{ $t('No offers found') }}</p>
                            <p class="text-gray-600">{{ $t('There are no offers available at the moment. Please check back later.') }}</p>
                        </div>
                    </div>

                    <div v-if="canLoadMore" class="col-span-full flex justify-center">
                        <button class="button flex w-[350px] justify-center" @click.prevent="loadMore">
                            {{ $t('Load more results') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <JobAlert ref="jobAlertRef" />
    </Layout>
</template>

<script setup>
    import JobAlert from '@/Partials/JobAlert.vue';
    import OfferCard from '@/Partials/OfferCard.vue';
    import CityAutocomplete from '@/Shared/CityAutocomplete.vue';
    import { useTypesenseOffers } from '@/useTypesenseOffers';
    import { usePage } from '@inertiajs/inertia-vue3';
    import { IconMapPin, IconSearch, IconX } from '@tabler/icons-vue';
    import { onClickOutside, useIntersectionObserver, useStorage, useUrlSearchParams } from '@vueuse/core';
    import { computed, onMounted, ref, useTemplateRef } from 'vue';

    const props = defineProps({
        company: Object,
        filters: Object,
        redirect: String,
    });

    let query = ref(props.filters.query || '');
    let city = ref(props.filters.city || '');
    let lat = ref(props.filters.lat || '');
    let lng = ref(props.filters.lng || '');

    // Sync URL params with query and city
    const params = useUrlSearchParams('history');

    // Typesense
    const { offers, search, loadMore, canLoadMore, loading } = useTypesenseOffers();

    // Infinite scroll
    // const loadMoreRef = ref();
    // useIntersectionObserver(loadMoreRef, ([entry], observerElement) => {
    //     if (entry?.isIntersecting) {
    //         loadMore();
    //     }
    // });

    // Saved searches
    const savedSearches = useStorage('saved-searches', []);
    const recentSavedSearches = computed(() => {
        return savedSearches.value.slice(-5).reverse();
    });

    // Dropdown for saved searches
    const showDropdown = ref(false);
    const clickOutsideTarget = useTemplateRef('clickOutsideTarget');
    onClickOutside(clickOutsideTarget, event => {
        showDropdown.value = false;
    });

    onMounted(() => {
        handleSearch(false);

        // We expect savedSearches to be an array of objects with the following structure:
        // { id: number, query: string, city: string, lat: string, lng: string }
        // If it's just an array of strings, we need to reset it
        if (savedSearches.value.length && typeof savedSearches.value[0] === 'string') {
            savedSearches.value = [];
        }
    });

    function handleSearch(shouldSaveSearch = true) {
        params.recherche = query.value;
        params.ville = city.value;

        showDropdown.value = false;
        search(query.value, lat.value, lng.value, 1, props.company?.slug);

        if (shouldSaveSearch) {
            saveSearch();
        }
    }

    function clearQuery() {
        query.value = '';
        params.recherche = '';

        handleSearch(false);
    }

    function clearCity() {
        city.value = '';
        lat.value = '';
        lng.value = '';

        params.ville = '';
        params.lat = '';
        params.lng = '';

        handleSearch(false);
    }

    function setLatLng(latlng) {
        city.value = latlng.city;
        lat.value = latlng.lat;
        lng.value = latlng.lng;

        params.ville = latlng.city;
        params.lat = latlng.lat;
        params.lng = latlng.lng;

        handleSearch();
    }

    function saveSearch() {
        if (!query.value) {
            return;
        }

        let savedData = {
            id: Date.now(),
            query: query.value,
            city: city.value,
            lat: lat.value,
            lng: lng.value,
        };

        const searchExists = savedSearches.value.find(search => {
            return search.query == savedData.query && search.city == savedData.city;
        });

        if (!searchExists) {
            savedSearches.value.push(savedData);
        }
    }

    function deleteSearchItem(searchId) {
        savedSearches.value = savedSearches.value.filter(search => {
            return search.id != searchId;
        });
    }

    function getSavedSearchUrl(search) {
        let baseUrl = usePage().props.value.locale == 'fr' ? '/emplois' : '/en/job-opportunities';
        let url = baseUrl + '?recherche=' + search.query;

        if (search.city) {
            url += '&ville=' + search.city + '&lat=' + search.lat + '&lng=' + search.lng;
        }

        return url;
    }
</script>
